import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import DropCursor from "@tiptap/extension-dropcursor";
import GapCursor from "@tiptap/extension-gapcursor";
import Placeholder from "@tiptap/extension-placeholder";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import React, { useCallback, useRef } from "react";
import { cn } from "../../lib/utils";
import { FloatingToolbar } from "../editor/FloatingToolbar";

interface TiptapEditorProps {
  content: string;
  onUpdate: (content: string) => void;
  onImageUpload?: (file: File) => Promise<string>;
  label?: string;
  disabled?: boolean;
  size?: "medium" | "large";
}

export function TiptapEditor({
  content,
  onUpdate,
  onImageUpload,
  label = "Message",
  disabled = false,
  size = "medium",
}: TiptapEditorProps) {
  const editorId = `tiptap-editor-${React.useId()}`;
  const editorContainerRef = React.useRef<HTMLDivElement>(null);
  const editorRef = useRef<any>(null);

  const handleImageUpload = useCallback(async (file: File) => {
    if (!onImageUpload || !editorRef.current) return;
    
    try {
      const imageUrl = await onImageUpload(file);
      editorRef.current?.chain().focus().setImage({ src: imageUrl }).run();
    } catch (error) {
      console.error("Erreur lors de l'upload de l'image:", error);
    }
  }, [onImageUpload]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      DropCursor,
      GapCursor,
      Image.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            width: {
              default: null,
              parseHTML: element => element.getAttribute('width'),
              renderHTML: attributes => {
                if (!attributes.width) {
                  return {};
                }
                return {
                  width: attributes.width,
                };
              },
            },
            height: {
              default: null,
              parseHTML: element => element.getAttribute('height'),
              renderHTML: attributes => {
                if (!attributes.height) {
                  return {};
                }
                return {
                  height: attributes.height,
                };
              },
            },
            alt: {
              default: null,
              parseHTML: element => element.getAttribute('alt'),
              renderHTML: attributes => {
                if (!attributes.alt) {
                  return {};
                }
                return {
                  alt: attributes.alt,
                };
              },
            },
            style: {
              default: null,
              parseHTML: element => element.getAttribute('style'),
              renderHTML: attributes => {
                if (!attributes.style) {
                  return {};
                }
                return {
                  style: attributes.style,
                };
              },
            },
          };
        },
        addNodeView() {
          return ({ node, getPos, editor: nodeEditor }) => {
            const img = document.createElement("img");
            const container = document.createElement("div");
            
            container.className = "relative inline-block group";
            img.src = node.attrs.src;
            img.alt = node.attrs.alt || "";
            img.className = "max-w-full h-auto cursor-pointer";
            
            if (node.attrs.width) img.width = node.attrs.width;
            if (node.attrs.height) img.height = node.attrs.height;
            if (node.attrs.style) img.style.cssText = node.attrs.style;

            const resizeHandle = document.createElement("div");
            resizeHandle.className = "absolute bottom-0 right-0 w-4 h-4 bg-blue-500 border-2 border-white cursor-nw-resize opacity-0 group-hover:opacity-100 transition-opacity";
            
            let isResizing = false;
            let startX = 0;
            let startWidth = 0;
            let startHeight = 0;

            resizeHandle.addEventListener("mousedown", (e) => {
              e.preventDefault();
              e.stopPropagation();
              isResizing = true;
              startX = e.clientX;

              startWidth = img.offsetWidth;
              startHeight = img.offsetHeight;
              document.addEventListener("mousemove", handleMouseMove);
              document.addEventListener("mouseup", handleMouseUp);
            });

            const handleMouseMove = (e: MouseEvent) => {
              if (!isResizing) return;
              
              const deltaX = e.clientX - startX;
              const aspectRatio = startHeight / startWidth;
              const newWidth = Math.max(50, startWidth + deltaX);
              const newHeight = newWidth * aspectRatio;
              
              img.style.width = `${newWidth}px`;
              img.style.height = `${newHeight}px`;
            };

            const handleMouseUp = () => {
              if (!isResizing) return;
              isResizing = false;
              
              const newWidth = parseInt(img.style.width);
              const newHeight = parseInt(img.style.height);
              
              if (typeof getPos === "function") {
                const pos = getPos();
                if (pos !== undefined) {
                  nodeEditor.commands.updateAttributes("image", {
                    width: newWidth,
                    height: newHeight,
                  });
                }
              }
              
              document.removeEventListener("mousemove", handleMouseMove);
              document.removeEventListener("mouseup", handleMouseUp);
            };

            container.appendChild(img);
            container.appendChild(resizeHandle);

            return {
              dom: container,
              update: (updatedNode) => {
                if (updatedNode.type.name !== "image") return false;
                img.src = updatedNode.attrs.src;
                img.alt = updatedNode.attrs.alt || "";
                if (updatedNode.attrs.width) {
                  img.style.width = `${updatedNode.attrs.width}px`;
                }
                if (updatedNode.attrs.height) {
                  img.style.height = `${updatedNode.attrs.height}px`;
                }
                if (updatedNode.attrs.style) {
                  img.style.cssText = updatedNode.attrs.style;
                }
                return true;
              },
            };
          };
        },
      }).configure({
        allowBase64: true,
        HTMLAttributes: {
          class: "max-w-full h-auto",
        },
      }),
      Placeholder.configure({
        placeholder: "Rédigez votre message...",
      }),
    ],
    content,
    shouldRerenderOnTransaction: false,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm",
          "focus:outline-none",
          "min-h-[10rem] max-w-none p-0 w-full",
          "placeholder:text-muted-foreground/70",
          size === "large" ? "text-lg" : "text-base",
          disabled && "opacity-50 cursor-not-allowed",
        ),
        id: editorId,
      },
      handleDrop: (_view, event, _slice, moved) => {
        if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
          const file = event.dataTransfer.files[0];
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            handleImageUpload(file);
            return true;
          }
        }
        return false;
      },
      handlePaste: (_view, event, _slice) => {
        const items = Array.from(event.clipboardData?.items || []);
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              event.preventDefault();
              handleImageUpload(file);
              return true;
            }
          }
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onUpdate(html);
    },
    editable: !disabled,
  });

  React.useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Sync external content changes with editor
  React.useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      // Use a timeout to ensure the editor is fully initialized
      const timer = setTimeout(() => {
        try {
          editor.commands.setContent(content);
        } catch (error) {
          console.warn("Failed to set editor content:", error);
          // Fallback: try to set content again after a short delay
          setTimeout(() => {
            try {
              editor.commands.setContent(content || "");
            } catch (fallbackError) {
              console.error("Failed to set editor content on fallback:", fallbackError);
            }
          }, 100);
        }
      }, 10);
      
      return () => clearTimeout(timer);
    }
    // Return undefined when no cleanup is needed
    return undefined;
  }, [content, editor]);

  const focusEditor = () => {
    editor?.commands.focus();
  };

  if (!editor) {
    return (
      <div className="w-full min-h-[12rem] animate-pulse bg-muted/20 rounded" />
    );
  }

  return (
    <div className="w-full">
      {label && (
        <div className="text-sm font-medium block text-foreground mb-1">
          <button
            type="button"
            onClick={focusEditor}
            className="bg-transparent border-0 p-0 text-inherit cursor-pointer font-inherit text-left w-full"
            disabled={disabled}
            tabIndex={-1}
          >
            {label}
          </button>
        </div>
      )}
      <div
        ref={editorContainerRef}
        className={cn(
          "relative w-full",
          "before:absolute before:bottom-0 before:left-0 before:h-px before:w-full before:bg-border",
          "after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-primary after:transition-all after:duration-200 after:ease-out focus-within:after:w-full",
        )}
      >
        <div className="px-2 py-1">
          <EditorContent editor={editor} className="prose-sm" />
          {!disabled && <FloatingToolbar editor={editor} />}
        </div>
      </div>
    </div>
  );
}
